import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { visualLog } from '@/components/debug/VisualDebugger';
import { getDeviceInfo } from '@/utils/deviceInfo';

/**
 * Hook che rileva lo stato della tastiera virtuale sia in ambiente web/PWA che su dispositivi Capacitor.
 * Utilizza la Visual Viewport API per misurare l'altezza effettiva del viewport
 * ed applica soglie dinamiche per determinare l'apertura della tastiera.
 *
 * @param threshold Rapporto minimo tra altezza attuale e iniziale per considerare la tastiera chiusa (default 0.6)
 * @returns Stato della tastiera e funzione per forzarne la chiusura
 */
export const useKeyboardState = (threshold: number = 0.5) => {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Altezza iniziale del viewport utilizzata come riferimento per calcolare l'altezza della tastiera
    const baseHeight = window.visualViewport?.height ?? window.innerHeight;
    const viewport = window.visualViewport;

    const info = getDeviceInfo();
    visualLog.info(
      `Device info: model ${info.model || 'n/a'} Android ${info.androidVersion} OneUI ${info.oneUIVersion}`
    );
    visualLog.info(`Initial viewport height: ${baseHeight}px`);
    if (info.isSamsungBrowser) {
      visualLog.info('Samsung Internet detected');
    }

    const safeTop = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top');
    const safeBottom = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-bottom');
    visualLog.info(`Safe areas top ${safeTop} bottom ${safeBottom}`);

    if (Capacitor.isPluginAvailable('Keyboard')) {
      Keyboard.getResizeMode().then(mode => {
        visualLog.info(`Keyboard resize mode: ${mode.mode}`);
      }).catch(() => {});
    }

    if ((Capacitor as any).isPluginAvailable?.('GestureNavigation')) {
      (Capacitor as any).Plugins?.GestureNavigation?.isEnabled?.().then((res: any) => {
        visualLog.info(`Gesture navigation: ${res?.enabled}`);
      }).catch(() => {
        visualLog.warn('Failed to get gesture navigation state');
      });
    }

    const isSamsung = info.isSamsungBrowser;

    let lastResizeTime = -1;

    const handleResize = (evt?: Event) => {
      if (isSamsung && evt?.timeStamp === lastResizeTime) {
        visualLog.warn('Duplicate resize event detected');
      }
      if (evt) lastResizeTime = evt.timeStamp;
      const currentHeight = viewport?.height ?? window.innerHeight;
      const ratio = currentHeight / baseHeight;
      const diffRatio = (baseHeight - currentHeight) / baseHeight;
      // Se la tastiera occupa metà schermo la consideriamo sicuramente aperta
      const isOpen = diffRatio > 0.5 || ratio < threshold;

      if (isOpen !== isKeyboardOpen) {
        visualLog.info(`Keyboard state changed: ${isOpen ? 'opened' : 'closed'}`);
        setIsKeyboardOpen(isOpen);
      }

      const heightDiff = Math.max(0, baseHeight - currentHeight);
      if (heightDiff !== keyboardHeight) {
        setKeyboardHeight(heightDiff);
        document.documentElement.style.setProperty('--keyboard-offset', `${heightDiff}px`);
      }
      const cssOffset = getComputedStyle(document.documentElement).getPropertyValue('--keyboard-offset');
      visualLog.info(
        `Viewport ${currentHeight}px ratio ${ratio.toFixed(2)} css-offset ${cssOffset}`
      );
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    viewport?.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      viewport?.removeEventListener('resize', handleResize);
    };
  }, [isKeyboardOpen, keyboardHeight, threshold]);

  const hideKeyboard = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
      visualLog.info('Keyboard hide requested');
      return true;
    }
    return false;
  };

  return {
    isKeyboardOpen,
    keyboardHeight,
    hideKeyboard
  };
};
